import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { createCheckoutSession, submitCorporateEnquiry } from '../api';
import { hasActiveAccess } from '../constants/roles';
import {
  SUBSCRIPTION_COUNTRIES,
  isSubscriptionEligibleCountry,
  isEuCountry,
  guessCountryCodeFromName,
} from '../constants/billingCountries';
import {
  CheckCircle2,
  Sparkles,
  ArrowLeft,
  ShieldCheck,
  Leaf,
  BarChart3,
  Map,
  Building2,
  FileBarChart,
  Users,
  Star,
  Globe,
} from 'lucide-react';
import { EcointelLottieMark } from './EcoIntelLoader';
import { useUserFacingError } from '../hooks/useUserFacingError';

// Sentinel for "my country isn't in the UK/EU list" — routes the user to the
// register-interest path instead of Stripe checkout (B2B brief 2026-06-11).
const COUNTRY_OUTSIDE = 'OTHER';

const FOUNDING_FEATURES = [
  { icon: Map, label: 'Full property-level satellite analytics' },
  { icon: BarChart3, label: 'EcoDynamic Index scoring & temporal tracking' },
  { icon: Leaf, label: 'Carbon flux estimation & health assessment' },
  { icon: ShieldCheck, label: 'Access to all current and upcoming features' },
];

const CORPORATE_FEATURES = [
  { icon: FileBarChart, label: 'CSRD / TCFD-aligned ESG reporting' },
  { icon: BarChart3, label: 'Multi-site portfolio analytics & benchmarking' },
  { icon: Users, label: 'Dedicated account manager & onboarding' },
  { icon: ShieldCheck, label: 'Audit-ready exports & verification support' },
];

export const Subscription = () => {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [enquiryStatus, setEnquiryStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [enquiryError, setEnquiryError] = useState(null);
  // Paid-subscription country gate (B2B brief 2026-06-11): UK + EU-27 only.
  const [country, setCountry] = useState('');
  const [geoCountryName, setGeoCountryName] = useState('');
  const [geoStatus, setGeoStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [geoError, setGeoError] = useState(null);
  const navigate = useNavigate();
  const { formatError, logError } = useUserFacingError();

  useEffect(() => {
    const check = async () => {
      if (!auth.currentUser) return;
      const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (snap.exists()) {
        const d = snap.data();
        if (hasActiveAccess(d.role, d.subscriptionStatus, d.subscriptionOverride)) {
          setAlreadyActive(true);
        }
        // Pre-select the eligibility dropdown from the saved profile address
        // when we can confidently map it; otherwise leave blank for the user
        // to choose. This only PRE-FILLS — it never bypasses the gate.
        const guess = guessCountryCodeFromName(d.addressCountry);
        if (guess) setCountry(guess);
      }
    };
    check();
  }, []);

  const countryChosen = !!country;
  const eligible = isSubscriptionEligibleCountry(country);

  const handleSubscribe = async (plan) => {
    if (!countryChosen) {
      setError('Please select your country to continue.');
      return;
    }
    if (!eligible) {
      setError('Paid subscriptions are currently available to UK and EU businesses only.');
      return;
    }
    setLoadingPlan(plan);
    setError(null);
    try {
      const { url } = await createCheckoutSession(plan);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      logError('Checkout error', err);
      setError(formatError(err) || 'Something went wrong. Please try again.');
      setLoadingPlan(null);
    }
  };

  // Out-of-region buyers can't check out — capture a "register interest"
  // enquiry via the existing corporate-ESG email route, tagged with country.
  const handleGeoInterest = async () => {
    setGeoStatus('sending');
    setGeoError(null);
    try {
      const named = SUBSCRIPTION_COUNTRIES.find((c) => c.code === country)?.name
        || geoCountryName.trim()
        || 'not specified';
      await submitCorporateEnquiry({
        message: `Register interest. Paid subscription requested from outside the UK/EU. Country: ${named}.`,
        country: country === COUNTRY_OUTSIDE ? COUNTRY_OUTSIDE : (country || ''),
        source: 'subscription_geo_block',
      });
      setGeoStatus('sent');
    } catch (err) {
      logError('Geo register-interest failed', err);
      setGeoError(formatError(err) || 'Could not send your request. Please try again.');
      setGeoStatus('error');
    }
  };

  const handleCorporateEnquiry = async () => {
    setEnquiryStatus('sending');
    setEnquiryError(null);
    try {
      await submitCorporateEnquiry({
        message: 'Interested in the Corporate ESG & Sustainability Reporting plan.',
      });
      setEnquiryStatus('sent');
    } catch (err) {
      logError('Corporate enquiry error', err);
      setEnquiryError(formatError(err) || 'Could not send your enquiry. Please try again.');
      setEnquiryStatus('error');
    }
  };

  if (alreadyActive) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={28} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">You're Already Subscribed</h2>
          <p className="text-slate-500 mb-6">Your account has full access to EcoIntel.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2.5 bg-[#043132] text-white font-medium rounded-xl hover:bg-[#065354] transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-8 transition-colors"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full mb-4">
          <Sparkles size={12} /> CHOOSE YOUR PLAN
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
          Plans built around your land
        </h2>
        <p className="text-slate-500 text-lg max-w-2xl mx-auto">
          From founding-associate single properties to corporate ESG portfolios. Pick the plan that fits how you manage and report on land.
        </p>
      </div>

      {/* Country eligibility gate — paid plans are UK + EU-27 only. The free
          demo is unaffected (handled elsewhere); this only gates checkout. */}
      <div className="max-w-md mx-auto mb-10">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
          <Globe size={13} className="text-[#043132]" /> Where is your business based?
        </label>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
        >
          <option value="" disabled>Select your country…</option>
          {SUBSCRIPTION_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
          <option value={COUNTRY_OUTSIDE}>Other (outside the UK &amp; EU)</option>
        </select>

        {countryChosen && eligible && isEuCountry(country) && (
          <p className="text-xs text-slate-500 mt-2">
            EU businesses: you'll need to enter a valid EU VAT number at checkout for reverse charge (0% VAT).
          </p>
        )}

        {countryChosen && !eligible && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-900 font-semibold">Paid subscriptions aren't available in your region yet</p>
            <p className="text-xs text-amber-800 mt-1">
              EcoIntel currently sells to UK and EU businesses only. Register your interest and we'll be in touch
              if that changes.
            </p>
            {country === COUNTRY_OUTSIDE && (
              <input
                type="text"
                value={geoCountryName}
                onChange={(e) => setGeoCountryName(e.target.value)}
                placeholder="Your country"
                className="w-full mt-3 border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
              />
            )}
            {geoStatus === 'sent' ? (
              <p className="text-sm text-emerald-800 font-medium mt-3 flex items-center gap-1.5">
                <CheckCircle2 size={15} /> Thanks, we've registered your interest.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleGeoInterest}
                disabled={geoStatus === 'sending'}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-amber-900 border border-amber-300 bg-white rounded-lg hover:bg-amber-100 disabled:opacity-60 transition-colors"
              >
                {geoStatus === 'sending' ? <EcointelLottieMark size={14} /> : null}
                {geoStatus === 'sending' ? 'Sending…' : 'Register interest'}
              </button>
            )}
            {geoStatus === 'error' && geoError && (
              <p className="text-xs text-red-700 mt-2 whitespace-pre-wrap break-words">{geoError}</p>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-6 md:gap-8 md:grid-cols-3 items-stretch">
        {/* ── Founding Associate — Monthly ── */}
        <PricingCard
          eyebrow="Founding Associate"
          title="Monthly"
          price="£55"
          cadence="/month + VAT"
          subline="Cancel anytime."
          scope="Property-level land analytics. The Corporate ESG & Sustainability Reporting package is sold separately."
          features={FOUNDING_FEATURES}
          ctaLabel="Subscribe Monthly"
          loading={loadingPlan === 'monthly'}
          onClick={() => handleSubscribe('monthly')}
          disabled={!!loadingPlan || !eligible}
        />

        {/* ── Founding Associate — Annual (recommended) ── */}
        <PricingCard
          eyebrow="Founding Associate"
          title="Annual"
          price="£600"
          cadence="/year + VAT"
          subline="Save £60 vs monthly. Locked-in founding rate."
          scope="Property-level land analytics. The Corporate ESG & Sustainability Reporting package is sold separately."
          features={FOUNDING_FEATURES}
          ctaLabel="Subscribe Annually"
          loading={loadingPlan === 'annual'}
          onClick={() => handleSubscribe('annual')}
          disabled={!!loadingPlan || !eligible}
          recommended
        />

        {/* ── Corporate ESG & Sustainability Reporting ── */}
        <PricingCard
          eyebrow={
            <>
              <Building2 size={12} /> Corporate
            </>
          }
          title="ESG & Sustainability Reporting"
          price="Custom"
          cadence=""
          subline="Tailored to your portfolio, frameworks and reporting calendar."
          scope="Includes the full Corporate ESG & Sustainability Reporting package, plus everything in Founding Associate."
          features={CORPORATE_FEATURES}
          ctaLabel={
            enquiryStatus === 'sent'
              ? 'Enquiry Received'
              : enquiryStatus === 'sending'
              ? 'Sending…'
              : 'Contact Us'
          }
          onClick={handleCorporateEnquiry}
          loading={enquiryStatus === 'sending'}
          disabled={enquiryStatus === 'sending' || enquiryStatus === 'sent'}
          variant="corporate"
          ctaIcon={enquiryStatus === 'sent' ? CheckCircle2 : null}
        />
      </div>

      {enquiryStatus === 'sent' && (
        <div className="max-w-md mx-auto mt-8 p-4 bg-green-50 rounded-lg border border-green-200 flex items-start gap-3">
          <CheckCircle2 size={18} className="text-green-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-900">Thanks, we've got your enquiry.</p>
            <p className="text-xs text-green-800 mt-1">
              Our team will be in touch shortly to discuss your portfolio and reporting needs.
            </p>
          </div>
        </div>
      )}

      {enquiryStatus === 'error' && enquiryError && (
        <div className="max-w-md mx-auto mt-8 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-700 whitespace-pre-wrap break-words">{enquiryError}</p>
        </div>
      )}

      {error && (
        <div className="max-w-md mx-auto mt-8 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-700 whitespace-pre-wrap break-words">{error}</p>
        </div>
      )}

      <p className="text-xs text-center text-slate-400 mt-10 max-w-2xl mx-auto">
        Secure payment via Stripe. Pricing may change for future subscribers, founding-associate
        rate is locked in at sign-up. Prices shown exclude VAT (B2B). UK customers pay 20% VAT on top.
        EU business customers must enter a valid EU VAT number at checkout for reverse charge (0% VAT).
        EcoIntel is a business-to-business service available to UK and EU businesses only.
      </p>
    </div>
  );
};

/* ──────────────────────────────────────────────────
 * PricingCard — single SaaS-style plan tile.
 * Subtle hover lift + border highlight; recommended
 * variant uses the EDDP primary (#043132) accent.
 * ────────────────────────────────────────────────── */
const PricingCard = ({
  eyebrow,
  title,
  price,
  cadence,
  subline,
  scope,
  features,
  ctaLabel,
  ctaIcon: CtaIcon,
  onClick,
  loading,
  disabled,
  recommended,
  variant,
}) => {
  const isRecommended = !!recommended;
  const isCorporate = variant === 'corporate';

  const cardClasses = [
    'group relative flex flex-col bg-white rounded-2xl overflow-hidden',
    'transition-all duration-200 ease-out',
    'hover:-translate-y-1 hover:shadow-xl',
    isRecommended
      ? 'border-2 border-[#043132] shadow-lg ring-1 ring-[#043132]/10'
      : 'border border-slate-200 shadow-sm hover:border-slate-300',
  ].join(' ');

  const ctaBaseClasses =
    'w-full py-3 px-4 font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98]';

  const ctaClasses = isCorporate
    ? `${ctaBaseClasses} bg-white border-2 border-[#043132] text-[#043132] hover:bg-[#043132] hover:text-white`
    : isRecommended
    ? `${ctaBaseClasses} bg-[#043132] hover:bg-[#065354] text-white shadow-md`
    : `${ctaBaseClasses} bg-slate-900 hover:bg-black text-white`;

  return (
    <div className={cardClasses}>
      {isRecommended && (
        <div className="absolute top-0 right-0">
          <div className="flex items-center gap-1 bg-[#043132] text-white text-[11px] font-bold px-3 py-1 rounded-bl-xl">
            <Star size={11} fill="currentColor" /> RECOMMENDED
          </div>
        </div>
      )}

      <div className="px-6 pt-6 pb-4">
        <div className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3">
          {eyebrow}
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-1">{title}</h3>
        <div className="flex items-baseline gap-1 mt-3">
          <span className="text-4xl font-bold text-slate-900">{price}</span>
          {cadence && <span className="text-slate-500 text-sm">{cadence}</span>}
        </div>
        {subline && <p className="text-xs text-slate-500 mt-2">{subline}</p>}
        {scope && (
          <p className={`text-xs mt-3 leading-relaxed ${isCorporate ? 'text-[#043132] font-medium' : 'text-slate-600 italic'}`}>
            {scope}
          </p>
        )}
      </div>

      <div className="px-6 pb-6 flex-1 flex flex-col">
        <ul className="space-y-3 mb-6 flex-1">
          {features.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-start gap-3 text-slate-700 text-sm">
              <Icon size={16} className="text-[#043132] mt-0.5 shrink-0" />
              <span>{label}</span>
            </li>
          ))}
        </ul>

        <button onClick={onClick} disabled={disabled} className={ctaClasses}>
          {loading ? (
            <>
              <EcointelLottieMark size={18} />
              {isCorporate ? 'Sending…' : 'Connecting to Stripe...'}
            </>
          ) : (
            <>
              {CtaIcon && <CtaIcon size={16} />}
              {ctaLabel}
            </>
          )}
        </button>
      </div>
    </div>
  );
};
